import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Eye, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PopularPosts = ({ 
  limit = 5, 
  showViewCount = true,
  variant = 'sidebar' // 'sidebar' | 'horizontal' | 'compact'
}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPopularPosts();
  }, [limit]);

  const fetchPopularPosts = async () => {
    try {
      const response = await axios.get(`${API}/popular/posts`, {
        params: { limit }
      });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching popular posts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-orange-400" />
            Popular Articles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(limit)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-800 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-800 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  // Horizontal variant for homepage
  if (variant === 'horizontal') {
    return (
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-orange-400" />
              Trending Articles
            </h2>
            <Link 
              to="/blog" 
              className="text-orange-400 hover:text-orange-300 flex items-center gap-1 transition-colors"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {posts.map((post, index) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.id}`}
                className="group"
                data-testid={`popular-post-${post.id}`}
              >
                <Card className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors h-full">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-sm">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium text-sm line-clamp-2 group-hover:text-orange-400 transition-colors">
                          {post.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <span className="text-orange-400/80">{post.category}</span>
                          {showViewCount && post.views > 0 && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {post.views}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <div className="space-y-2" data-testid="popular-posts-compact">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-orange-400" />
          Trending
        </h3>
        {posts.slice(0, 3).map((post, index) => (
          <Link 
            key={post.id} 
            to={`/blog/${post.id}`}
            className="block text-sm text-gray-300 hover:text-orange-400 transition-colors line-clamp-1"
          >
            <span className="text-orange-400 mr-2">{index + 1}.</span>
            {post.title}
          </Link>
        ))}
      </div>
    );
  }

  // Default sidebar variant
  return (
    <Card className="bg-gray-900 border-gray-800" data-testid="popular-posts-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5 text-orange-400" />
          Popular Articles
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {posts.map((post, index) => (
          <Link 
            key={post.id} 
            to={`/blog/${post.id}`}
            className="group flex items-start gap-3"
            data-testid={`popular-post-sidebar-${post.id}`}
          >
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-xs">
              {index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <h4 className="text-white text-sm font-medium line-clamp-2 group-hover:text-orange-400 transition-colors">
                {post.title}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-orange-400/80">{post.category}</span>
                {showViewCount && post.views > 0 && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    {post.views} views
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
        
        <Link 
          to="/blog" 
          className="block text-center text-sm text-orange-400 hover:text-orange-300 pt-2 border-t border-gray-800"
        >
          View all articles →
        </Link>
      </CardContent>
    </Card>
  );
};

export default PopularPosts;
